/**
 * Runtime Configuration Helper
 *
 * This module provides a centralized way to access runtime configuration.
 * It falls back to build-time environment variables for development mode.
 */

/**
 * Every runtime-injectable configuration key.
 * Keep this list in sync with `public/config.js` and `scripts/inject-config.sh`.
 */
export type RuntimeConfigKey =
  // API endpoints
  | "REACT_APP_CONFIG_API_HOST"
  | "REACT_APP_ENGINE_API_HOST"
  | "REACT_APP_ENGINE_SOCKET_HOST"
  | "REACT_APP_PAGER_DUTY_HOST"
  | "REACT_APP_GRAFANA_BASE_URL"
  | "REACT_APP_REPORT_DOWNLOAD_PATH"
  // Application settings
  | "REACT_APP_HOST"
  | "REACT_APP_HOST_BASE_PATH"
  | "REACT_APP_BASE_FILE_PATH"
  | "REACT_APP_DEFAULT_ROUTE"
  | "REACT_APP_SIDEBAR_V2"
  | "REACT_APP_E2E_MODE"
  // Pagination
  | "REACT_APP_DEFAULT_PAGE"
  | "REACT_APP_DEFAULT_PAGE_SIZE"
  // Timing / session
  | "REACT_APP_DEBOUNCE_DELAY"
  | "REACT_APP_RETRY_DELAY"
  | "REACT_APP_TIMEOUT_DURATION"
  | "REACT_APP_TOAST_TIMEOUT_DURATION"
  | "REACT_APP_SESSION_WARNING_THRESHOLD"
  | "REACT_APP_TIMELINE_UPDATE_INTERVAL"
  | "REACT_APP_TOKEN_EXPIRY_LOGOUT_THRESHOLD"
  | "REACT_APP_PROACTIVE_REFRESH_THRESHOLD"
  // Uploads / research queries
  | "REACT_APP_UPLOAD_FILE_SIZE"
  | "REACT_APP_RESEARCH_QUERY_MAXIMUM_SUB_QUERY"
  // Output storage defaults
  | "REACT_APP_OUTPUT_FILES_BASE_PATH"
  | "REACT_APP_OUTPUT_STORAGE_LINKED_SERVICE_ID"
  | "REACT_APP_OUTPUT_STORAGE_LINKED_SERVICE_CODE"
  | "REACT_APP_OUTPUT_STORAGE_CONNECTION_KEY"
  // Execution defaults
  | "REACT_APP_DEFAULT_RUN_INSTANCE_ID"
  | "REACT_APP_DEFAULT_RUN_INSTANCE_NAME"
  | "REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_ID"
  | "REACT_APP_DEFAULT_EXECUTION_SUB_GROUP_CODE"
  // Downloads
  | "REACT_APP_DOWNLOAD_FILES_BASE_PATH"
  | "REACT_APP_DOWNLOAD_LOG_URL"
  | "REACT_APP_DOWNLOAD_LOG_URL_1"
  | "REACT_APP_DOWNLOAD_LOG_URL_2"
  | "REACT_APP_DOWNLOAD_LOG_URL_3"
  | "REACT_APP_DOWNLOAD_LOG_URL_4"
  | "REACT_APP_DOWNLOAD_LOG_URL_5"
  // GraphQL toggles
  | "REACT_APP_DOMAIN_API"
  | "REACT_APP_RESOURCE_API"
  // License keys
  | "REACT_APP_DATA_GRID_PRO"
  // Scoring theme
  | "REACT_APP_SCORE_THRESHOLD_GOOD"
  | "REACT_APP_SCORE_THRESHOLD_FAIR"
  | "REACT_APP_SCORE_LABEL_GOOD"
  | "REACT_APP_SCORE_LABEL_FAIR"
  | "REACT_APP_SCORE_LABEL_POOR"
  | "REACT_APP_SCORE_COLOR_GOOD"
  | "REACT_APP_SCORE_COLOR_FAIR"
  | "REACT_APP_SCORE_COLOR_POOR"
  | "REACT_APP_SCORE_BG_GOOD"
  | "REACT_APP_SCORE_BG_FAIR"
  | "REACT_APP_SCORE_BG_POOR"
  | "REACT_APP_SCORE_LOW_BAR_GRADIENT"
  | "REACT_APP_SCORE_MED_BAR_GRADIENT"
  | "REACT_APP_SCORE_HIGH_BAR_GRADIENT"
  | "REACT_APP_SCORE_LOW_CHIP_BG"
  | "REACT_APP_SCORE_LOW_CHIP_COLOR"
  | "REACT_APP_SCORE_MED_CHIP_BG"
  | "REACT_APP_SCORE_MED_CHIP_COLOR"
  | "REACT_APP_SCORE_HIGH_CHIP_BG"
  | "REACT_APP_SCORE_HIGH_CHIP_COLOR"
  | "REACT_APP_SCORE_METRIC_ACCENT"
  | "REACT_APP_SCORE_SLIDER_ACCENT"
  | "REACT_APP_SCORE_LOW_IMPACT_COLOR"
  | "REACT_APP_SCORE_HIGH_IMPACT_COLOR";

declare global {
  interface Window {
    RUNTIME_CONFIG?: Partial<Record<RuntimeConfigKey, string>>;
  }
}

/**
 * Read a runtime-injected configuration value, falling back to the build-time
 * environment variable (development) and finally to `defaultValue`.
 */
export const getConfig = (
  key: RuntimeConfigKey,
  defaultValue: string = "",
): string => {
  return getOptionalConfig(key) ?? defaultValue;
};

/**
 * Same as `getConfig` but returns `undefined` when the value is not configured,
 * for call sites that must distinguish "unset" from an empty string.
 */
export const getOptionalConfig = (
  key: RuntimeConfigKey,
): string | undefined => {
  // First check runtime config (injected at container start)
  const runtimeValue = window.RUNTIME_CONFIG?.[key];
  // Skip values that are still un-substituted template placeholders
  if (runtimeValue && !runtimeValue.startsWith("${")) {
    return runtimeValue;
  }

  // Fall back to build-time environment variable (for development)
  if (process.env[key]) {
    return process.env[key] as string;
  }

  return undefined;
};

/**
 * Configuration object with all runtime values
 */
export const RuntimeConfig = {
  // API Endpoints
  CONFIG_API_HOST: getConfig(
    "REACT_APP_CONFIG_API_HOST",
    "http://localhost:4000",
  ),
  ENGINE_API_HOST: getConfig(
    "REACT_APP_ENGINE_API_HOST",
    "http://localhost:5000",
  ),
  ENGINE_SOCKET_HOST: getConfig(
    "REACT_APP_ENGINE_SOCKET_HOST",
    "http://localhost:5000",
  ),
  PAGER_DUTY_HOST: getConfig("REACT_APP_PAGER_DUTY_HOST", ""),
  GRAFANA_BASE_URL: getConfig("REACT_APP_GRAFANA_BASE_URL", ""),

  // Application Settings
  HOST: getConfig("REACT_APP_HOST", "http://localhost:3000"),
  HOST_BASE_PATH: getConfig("REACT_APP_HOST_BASE_PATH", "/"),

  // Feature Flags & Settings
  DEFAULT_PAGE: getConfig("REACT_APP_DEFAULT_PAGE", "0"),
  DEFAULT_PAGE_SIZE: getConfig("REACT_APP_DEFAULT_PAGE_SIZE", "10"),
  DEBOUNCE_DELAY: getConfig("REACT_APP_DEBOUNCE_DELAY", "400"),
  RETRY_DELAY: getConfig("REACT_APP_RETRY_DELAY", "4000"),
  TIMEOUT_DURATION: getConfig("REACT_APP_TIMEOUT_DURATION", "900000"),
  SESSION_WARNING_THRESHOLD: getConfig(
    "REACT_APP_SESSION_WARNING_THRESHOLD",
    "1",
  ),
  TIMELINE_UPDATE_INTERVAL: getConfig(
    "REACT_APP_TIMELINE_UPDATE_INTERVAL",
    "60000",
  ),
  RESEARCH_QUERY_MAXIMUM_SUB_QUERY: getConfig(
    "REACT_APP_RESEARCH_QUERY_MAXIMUM_SUB_QUERY",
    "5",
  ),
  DOWNLOAD_FILES_BASE_PATH: getConfig(
    "REACT_APP_DOWNLOAD_FILES_BASE_PATH",
    "C:\\inetpub\\wwwroot\\VIS\\download-files",
  ),
  DOWNLOAD_LOG_URL: getConfig(
    "REACT_APP_DOWNLOAD_LOG_URL",
    "EngineLogs/logs1.log",
  ),
  DOWNLOAD_LOG_URL_1: getConfig(
    "REACT_APP_DOWNLOAD_LOG_URL_1",
    "EngineLogs/logs1.log",
  ),
  DOWNLOAD_LOG_URL_2: getConfig(
    "REACT_APP_DOWNLOAD_LOG_URL_2",
    "EngineLogs/logs1.log.1",
  ),
  DOWNLOAD_LOG_URL_3: getConfig(
    "REACT_APP_DOWNLOAD_LOG_URL_3",
    "EngineLogs/logs1.log.2",
  ),
  DOWNLOAD_LOG_URL_4: getConfig(
    "REACT_APP_DOWNLOAD_LOG_URL_4",
    "EngineLogs/logs1.log.3",
  ),
  DOWNLOAD_LOG_URL_5: getConfig(
    "REACT_APP_DOWNLOAD_LOG_URL_5",
    "EngineLogs/logs1.log.4",
  ),

  // License Keys
  DATA_GRID_PRO: getConfig("REACT_APP_DATA_GRID_PRO", ""),
};

export default RuntimeConfig;
